import { useEffect, useRef } from 'react'
import { ROULETTE_WHEEL_NUMBERS } from '@/config/original'
import { AssetManager } from '../core/AssetManager'
import { SoundManager } from '../core/SoundManager'

interface RouletteWheelProps {
	winningNumber: number
	isSpinning: boolean
	onAnimationComplete: () => void
}

type WheelEngineState = 0 | 1 | 2

class WheelEngine {
	private readonly sectorAngle = 360 / 37
	private width = 400
	private height = 400
	private canvas: HTMLCanvasElement | null = null
	private container: HTMLElement | null = null
	private context: CanvasRenderingContext2D | null = null
	private radius = 200
	private wheelAngle = 0
	private ballAngle = 0
	private ballSpeed = 8
	private wheelSpeed = 0.5
	private readonly normalSpeed = 0.5
	private readonly initialBallSpeed = 8
	private readonly ballDeceleration = 0.995
	private targetBallAngle = 0
	private ballRadius = 250
	private currentBallRadius = 250
	private state: WheelEngineState = 0
	private animationFrameId = 0
	private resizeObserver: ResizeObserver | null = null
	private onAnimationComplete: (() => void) | null = null
	private disposed = false

	setOnAnimationComplete(callback: (() => void) | null): void {
		this.onAnimationComplete = callback
	}

	attach(container: HTMLElement, canvas: HTMLCanvasElement): void {
		this.container = container
		this.canvas = canvas
		this.context = canvas.getContext('2d')
		this.syncSize()

		this.resizeObserver = new ResizeObserver(() => {
			this.syncSize()
		})
		this.resizeObserver.observe(container)

		const tick = () => {
			if (this.disposed) {
				return
			}
			this.drawWheelAndBall()
			this.animationFrameId = window.requestAnimationFrame(tick)
		}

		this.animationFrameId = window.requestAnimationFrame(tick)
	}

	startSpin(winningNumber: number): void {
		if (this.state === 1) {
			return
		}

		this.state = 1
		this.ballSpeed = this.initialBallSpeed
		this.ballRadius = this.radius * 0.8
		this.currentBallRadius = this.radius * 0.8
		this.wheelSpeed = this.normalSpeed
		this.ballAngle = 0
		this.targetBallAngle = this.getBallTargetAngle(winningNumber)
		SoundManager.instance.play('wheelSpin')
	}

	dispose(): void {
		this.disposed = true
		window.cancelAnimationFrame(this.animationFrameId)
		this.resizeObserver?.disconnect()
		this.resizeObserver = null
		SoundManager.instance.stop('wheelSpin')
		this.canvas = null
		this.container = null
		this.context = null
		this.onAnimationComplete = null
	}

	private syncSize(): void {
		if (!this.container || !this.canvas) {
			return
		}

		this.width = this.container.offsetWidth
		this.height = this.container.offsetHeight
		this.canvas.width = this.width
		this.canvas.height = this.height
		this.radius = this.container.offsetWidth / 2

		if (this.ballRadius !== this.radius * 0.8) {
			this.ballRadius = this.radius * 0.8
			this.currentBallRadius = this.radius * 0.8
		}
	}

	private getWheelIndex(winningNumber: number): number {
		return ROULETTE_WHEEL_NUMBERS.findIndex((entry) => entry.number === winningNumber)
	}

	private getBallTargetAngle(winningNumber: number): number {
		const index = this.getWheelIndex(winningNumber)
		return index * this.sectorAngle
	}

	private drawWheel(angle: number): void {
		const context = this.context
		const wheelImage = AssetManager.getWheelImage()
		if (!context || !wheelImage) {
			return
		}

		context.save()
		context.translate(this.width / 2, this.height / 2)
		context.rotate((angle * Math.PI) / 180)
		context.drawImage(wheelImage, -this.radius, -this.radius, this.radius * 2, this.radius * 2)
		context.restore()
	}

	private drawBall(angle: number): void {
		const context = this.context
		if (!context) {
			return
		}

		if (this.state === 2 && this.currentBallRadius > this.radius * 0.6) {
			this.currentBallRadius -= 3
		}

		const ballX = this.width / 2 + this.currentBallRadius * Math.cos((angle * Math.PI) / 180)
		const ballY = this.height / 2 + this.currentBallRadius * Math.sin((angle * Math.PI) / 180)

		const gradient = context.createRadialGradient(ballX, ballY, this.radius * 0.04 * 0.1, ballX, ballY, this.radius * 0.04)
		gradient.addColorStop(0, 'white')
		gradient.addColorStop(1, 'gray')

		context.beginPath()
		context.arc(ballX, ballY, this.radius * 0.04, 0, 2 * Math.PI)
		context.fillStyle = gradient
		context.fill()
	}

	private drawWheelAndBall(): void {
		if (!this.context) {
			return
		}

		if (this.state !== 0) {
			if (this.ballSpeed > 1) {
				this.ballAngle = (this.ballAngle + this.ballSpeed) % 360
				this.ballSpeed *= this.ballDeceleration
			} else {
				const delta = (this.targetBallAngle - this.ballAngle + 270) % 360

				if (delta > 1) {
					this.ballAngle += Math.min(delta, this.ballSpeed)
					this.ballAngle %= 360
				} else if (this.state !== 2) {
					this.state = 2
					this.ballSpeed = 0
					SoundManager.instance.stop('wheelSpin')
					this.onAnimationComplete?.()
				}
			}
		}

		this.wheelAngle = (this.wheelAngle - this.wheelSpeed + 360) % 360
		this.context.clearRect(0, 0, this.width, this.height)

		const relativeAngle = (this.ballAngle + this.wheelAngle + 360) % 360
		this.drawWheel(this.wheelAngle)

		if (this.state !== 0) {
			this.drawBall(relativeAngle)
		}
	}
}

export function RouletteWheel({ winningNumber, isSpinning, onAnimationComplete }: RouletteWheelProps) {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const engineRef = useRef<WheelEngine | null>(null)
	const onCompleteRef = useRef(onAnimationComplete)

	useEffect(() => {
		onCompleteRef.current = onAnimationComplete
	}, [onAnimationComplete])

	useEffect(() => {
		const container = containerRef.current
		const canvas = canvasRef.current
		if (!container || !canvas) {
			return undefined
		}

		const engine = new WheelEngine()
		engine.setOnAnimationComplete(() => {
			onCompleteRef.current()
		})
		engine.attach(container, canvas)
		engineRef.current = engine

		return () => {
			engine.dispose()
			engineRef.current = null
		}
	}, [])

	useEffect(() => {
		if (isSpinning && winningNumber >= 0) {
			engineRef.current?.startSpin(winningNumber)
		}
	}, [isSpinning, winningNumber])

	return (
		<div ref={containerRef} className='h-full w-full'>
			<canvas ref={canvasRef} />
		</div>
	)
}
